
// #################################################

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PieChart from "examples/Charts/PieChart";
import ChartHeader from "examples/Charts/PieChart/ChartHeader"; // ✅ Import new header
import { generateColors } from "../../../utils/chartColors";
import Icon from "@mui/material/Icon";
import MetricCardsContainer from "../components/MetricCardsContainer";
import { Typography } from "@mui/material"; // ✅ Import Typography

import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";

import EntitiesPopup from "layouts/components/EntitiesPopup"; // ✅ Import the popup

import ChartAssistant from "components/ChartAssistant"; // ✅ Import ChartAssistant

// Helper to determine content type
const getContentType = (item, platform) => {
  if (platform === "Facebook") {
    return item.post_type;
  }
  if (platform === "YouTube") {
    return item.video_type;
  }
  if (platform === "Instagram") {
    return item.media_type;
  }
  return "Unknown";
};

// ✅ ADDED is_derived prop
function ContentTypeChart({
  title,
  datasets,
  previousdatasets = [],
  isLoading = false,
  is_derived = false,
  entitlements
}) {

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupFilter, setPopupFilter] = useState({ profileId: null, contentType: null });

  // 1. Calculate Pie Chart Data per Profile (Counts)
  const chartDataByProfile = useMemo(() => {
    if (!datasets || datasets.length === 0) {
      return [];
    }

    // Collect all unique content types first to assign consistent colors
    const allContentTypes = new Set();
    datasets.forEach(profile => {
      if (!profile) return;
      const contentField = profile.platform === "YouTube" ? "videos" : "posts";
      (profile.data || []).flatMap(entry => entry[contentField] || []).forEach(content => {
        allContentTypes.add(getContentType(content, profile.platform));
      });
    });

    const uniqueContentTypes = Array.from(allContentTypes).sort();
    const contentTypeColors = generateColors(uniqueContentTypes.length, uniqueContentTypes);
    const colorMap = uniqueContentTypes.reduce((map, type, index) => {
      map[type] = contentTypeColors[index];
      return map;
    }, {});

    return datasets.map((profile) => {
      if (!profile) return null;

      const contentTypeCounts = {};
      const contentField = profile.platform === "YouTube" ? "videos" : "posts";
      const contentArray = (profile.data || []).flatMap((entry) => entry[contentField] || []);

      contentArray.forEach((content) => {
        const type = getContentType(content, profile.platform);
        contentTypeCounts[type] = (contentTypeCounts[type] || 0) + 1; // Count!
      });

      const labels = Object.keys(contentTypeCounts);
      const data = Object.values(contentTypeCounts);
      const backgroundColors = labels.map((label) => colorMap[label] || "#333");

      return {
        profileName: profile.name || profile.label || "Unknown",
        platform: profile.platform,
        profileId: profile.profileId,
        chart: {
          labels,
          datasets: {
            label: "Content Type Distribution",
            data,
            backgroundColors,
          },
        },
      };
    }).filter(Boolean);
  }, [datasets]);

  // 2. Calculate Top Metrics (Total Counts)
  const contentMetrics = useMemo(() => {
    if (!datasets) return [];
    return datasets.map((profile) => {
      if (!profile) return null;
      const contentField = profile.platform === "YouTube" ? "videos" : "posts";

      // Calculate total count for this profile in the current period
      const currentCount = (profile.data || []).flatMap((entry) => entry[contentField] || []).length;

      // Calculate total count for the previous period
      const previousProfile = previousdatasets?.find((p) => p.profileId === profile.profileId);
      const previousCount = previousProfile
        ? (previousProfile.data || []).flatMap((entry) => entry[contentField] || []).length
        : 0;

      return {
        profilePic: profile.profilePictureUrl || "https://via.placeholder.com/150",
        name: profile.name || profile.label || "Unknown",
        platform: profile.platform,
        metricName: profile.platform === "YouTube" ? "Total Videos" : "Total Posts",
        metricValue: currentCount.toLocaleString(),
        previousMetricValue: previousCount.toLocaleString(),
      };
    }).filter(Boolean);
  }, [datasets, previousdatasets]);


  // ⬇️=========================================
  // ⬇️ ADDED: Synthetic Chart Data for AI
  // ⬇️=========================================
  const aiChartData = useMemo(() => {
    if (!chartDataByProfile || chartDataByProfile.length === 0) return null;

    // 1. Collect Union of all Labels
    const allLabels = new Set();
    chartDataByProfile.forEach(p => {
      p.chart.labels.forEach(l => allLabels.add(l));
    });
    const labels = Array.from(allLabels).sort();

    // 2. Build Datasets
    const datasets = chartDataByProfile.map(p => {
      return {
        label: `${p.profileName} (${p.platform})`,
        data: labels.map(label => {
          const idx = p.chart.labels.indexOf(label);
          return idx >= 0 ? p.chart.datasets.data[idx] : 0;
        })
      };
    });

    return { labels, datasets };
  }, [chartDataByProfile]);

  // ⬇️=========================================
  // ⬇️ ADDED: Text Context for AI
  // ⬇️=========================================
  const aiContext = useMemo(() => {
    let text = `Chart Description: Content Type breakdown per profile. Shows the number of posts/videos of each type (Photo, Video, etc.).\n\nInsights:`;
    if (chartDataByProfile) {
      chartDataByProfile.forEach(p => {
        text += `\n\nProfile: ${p.profileName} (${p.platform})`;
        p.chart.labels.forEach((l, i) => {
          text += `\n- ${l}: ${p.chart.datasets.data[i]}`;
        });
      });
    }
    return text;
  }, [chartDataByProfile]);

  // Click handlers
  const handleSliceClick = (contentType, profileId) => {
    setPopupFilter({ profileId, contentType });
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupFilter({ profileId: null, contentType: null });
  };

  // Loader and empty checks
  if (isLoading) {
    return (
      <Card>
        <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
          <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
      </Card>
    );
  }

  if (!datasets) {
    return (
      <Card>
        <MDBox p={3} textAlign="center">
          <MDTypography variant="body2" color="text">
            No data available.
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case "facebook":
        return <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
      case "youtube":
        return <YouTubeIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
      case "instagram":
        return <InstagramIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
      case "twitter":
        return <TwitterIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
      default:
        return null;
    }
  };

  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This chart shows the breakdown of content types (e.g., Photos, Videos, Links) posted by each profile.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - Each slice represents a specific content type. <br />
        - The size of the slice corresponds to the number of posts of that type.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Understand the content strategy of each profile. <br />
        - Compare which formats are favored by competitors. <br />
        - Click a slice to see the actual posts of that type.
      </Typography>
    </div>
  );

  return (
    <>
      <Card sx={{ display: "flex", flexDirection: "column" }}>
        {/* ⬇️ REPLACED HEADER ⬇️ */}
        <ChartHeader
          title={title}
          description={tooltipContent}
          profilePictures={contentMetrics.map(m => m.profilePic)}
          is_derived={is_derived}
        >
          {/* ✅ AI Assistant */}
          <ChartAssistant
            title={title}
            context={aiContext}
            chartData={aiChartData}
            rawData={datasets}
            sx={{
              position: "static",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              ml: 2
            }}
            entitlements={entitlements} // ✅ Pass entitlements
          />
        </ChartHeader>
        {/* ⬆️ REPLACED HEADER ⬆️ */}

        <MetricCardsContainer metrics={contentMetrics} />
        <MDBox mt={1} />

        <MDBox p={3}>
          <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
            {chartDataByProfile.map(({ profileName, platform, chart, profileId }, idx) => (
              <React.Fragment key={idx}>
                {chart.labels.length > 0 ? (
                  <MDBox
                    textAlign="center"
                    width={{ xs: "100%", md: "45%", lg: "30%" }}
                  >
                    <MDTypography
                      variant="subtitle2"
                      color="text"
                      mb={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={0.5}
                    >
                      {getPlatformIcon(platform)}{profileName}
                    </MDTypography>
                    <PieChart
                      chart={chart}
                      height="15rem"
                      icon={{ component: "", color: "" }}
                      title=""
                      description=""
                      onSliceClick={(contentType) => handleSliceClick(contentType, profileId)} // ✅ Pass handler
                    />
                    {/* Calculate if there is any data to show legend */}
                    {chart.datasets.data.reduce((a, b) => a + b, 0) > 0 && (
                      <MDBox display="flex" flexWrap="wrap" justifyContent="center" mt={1} px={3}>
                        {chart.labels.map((label, index) => (
                          <MDBox
                            key={label}
                            display="flex"
                            alignItems="center"
                            my={0.5}
                            mr={3}
                            minWidth="10%"
                          >
                            <Icon sx={{ color: chart.datasets.backgroundColors[index], mr: 1, fontSize: "1rem" }}>
                              fiber_manual_record
                            </Icon>
                            <MDTypography variant="button" color="text">
                              {label}: {chart.datasets.data[index].toLocaleString()}
                            </MDTypography>
                          </MDBox>
                        ))}
                      </MDBox>
                    )}
                  </MDBox>
                ) : (
                  <MDBox
                    textAlign="center"
                  >
                    <MDTypography
                      variant="subtitle2"
                      color="text"
                      mb={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={0.5}
                    >
                      {getPlatformIcon(platform)}{profileName}
                    </MDTypography>
                    <MDTypography variant="subtitle1" color="text" align="center">
                      No content found in the selected date range.
                    </MDTypography>
                  </MDBox>
                )}
              </React.Fragment>
            ))}
          </MDBox>
        </MDBox>

        {/* ✅ Disclaimer Footer */}
        {is_derived && (
          <MDBox p={1} display="flex" justifyContent="flex-end" sx={{ mt: "auto" }}>
            <MDTypography
              variant="caption"
              color="text"
              sx={{
                fontStyle: "italic",
                fontSize: "0.7rem",
                opacity: 0.7,
                mr: 1
              }}
            >
              *Metric calculated by Comparedly
            </MDTypography>
          </MDBox>
        )}
      </Card>

      {/* ✅ ADDED: Render the popup */}
      {isPopupOpen && (
        <EntitiesPopup
          isModalOpen={isPopupOpen}
          onClose={handlePopupClose}
          datasets={datasets}
          // Pass the new filter props
          filterProfileId={popupFilter.profileId}
          filterContentType={popupFilter.contentType}
        />
      )}
    </>
  );
}

// ✅ UPDATED PropTypes
ContentTypeChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  datasets: PropTypes.array.isRequired,
  previousdatasets: PropTypes.array,
  isLoading: PropTypes.bool,
  is_derived: PropTypes.bool,
  entitlements: PropTypes.object,
};

export default ContentTypeChart;